import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CreateAnnouncementRequest } from '@app/generated-api';
import { CreateAnnouncementResponse } from '@app/generated-api';
import { GetActiveAnnouncementsResponse } from '@app/generated-api';
import { GetAllAnnouncementsResponse } from '@app/generated-api';
import { GetAnnouncementByIdResponse } from '@app/generated-api';
import { UpdateAnnouncementRequest } from '@app/generated-api';
import { UpdateAnnouncementResponse } from '@app/generated-api';
import { createLazyLoadedState } from '@app/v2/utility';
import { getSiteBaseUrl } from '@utility/utility';
import { map, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class AnnouncementService {
    private readonly http = inject(HttpClient);

    readonly announcements = createLazyLoadedState(() => ({
        fetch$: () => this.http.get<GetAllAnnouncementsResponse[]>(`${getSiteBaseUrl()}/api/v2/announcements`),
    }));

    readonly announcement = createLazyLoadedState(() => ({
        fetch$: (id: number) =>
            this.http.get<GetAnnouncementByIdResponse>(`${getSiteBaseUrl()}/api/v2/announcements/${id}`),
    }));

    public getActiveAnnouncements(count: number) {
        return this.http.get<GetActiveAnnouncementsResponse[]>(`${getSiteBaseUrl()}/api/v2/announcements/active`, {
            params: {
                count: count,
            },
        });
    }

    public delete(announcementId: number): Observable<boolean> {
        return this.http
            .delete<boolean>(`${getSiteBaseUrl()}/api/v2/announcements/${announcementId}`, { observe: 'response' })
            .pipe(
                map((response) => {
                    if (response.status == 204) return true;
                    return false;
                })
            );
    }

    public create(announcement: CreateAnnouncementRequest): Observable<CreateAnnouncementResponse> {
        return this.http.post<CreateAnnouncementResponse>(`${getSiteBaseUrl()}/api/v2/announcements`, announcement);
    }

    public update(announcementId: number, announcement: UpdateAnnouncementRequest) {
        return this.http.put<UpdateAnnouncementResponse>(
            `${getSiteBaseUrl()}/api/v2/announcements/${announcementId}`,
            announcement
        );
    }

    public getAnnouncementsToDisplay(): Observable<GetActiveAnnouncementsResponse[]> {
        const existingIds = this.getAnnouncementIdsFromSessionStorage();
        return this.getActiveAnnouncements(2).pipe(
            map((announcements) =>
                existingIds ? announcements.filter((x) => !existingIds.includes(x.id)) : announcements
            )
        );
    }

    public getAnnouncementIdsFromSessionStorage() {
        const announcementIds = sessionStorage
            .getItem('announcements')
            ?.split(',')
            .map((x) => +x);
        return announcementIds;
    }

    public setAnnouncementIdsIntoSessionStorage(announcementIds: number[]) {
        //list existing ids in the session storage
        let existingIds = this.getAnnouncementIdsFromSessionStorage();

        if (existingIds != undefined && existingIds.length > 0) {
            //exclude ids that are allready in the array
            existingIds = existingIds.filter((x) => !announcementIds.includes(x));
            if (existingIds.length > 0) {
                //create single list to pass it to the sessionStorage
                announcementIds = announcementIds.concat(existingIds);
            }
        }
        //update session storage with new list of ids
        sessionStorage.setItem('announcements', announcementIds.toString());
    }
}
